import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Pencil, Plus, RotateCcw, Save, Search, Trash2 } from 'lucide-react'
import { DataTable } from './data-table'
import { SectionCard } from './section-card'
import { StatusBadge } from './status-badge'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { ImageUpload } from '../cms/image-upload'
import { FileUpload } from './file-upload'
import { RichTextEditor } from './rich-text-editor'
import { ConfirmDeleteDialog } from './confirm-delete-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
} from '../ui/dialog'

type FieldType = 'text' | 'textarea' | 'richtext' | 'number' | 'select' | 'date' | 'image' | 'file' | 'boolean' | 'json'

interface CrudField {
  key: string
  label: string
  type?: FieldType
  placeholder?: string
  options?: string[]
  readOnly?: boolean
}

interface CrudWorkspaceProps<T extends { id?: string; updatedAt?: unknown }> {
  title: string
  description: string
  fields: CrudField[]
  items: T[]
  onSave: (item: Record<string, any>) => void
  onDelete: (id: string) => void
  onReset?: () => void
  emptyTitle: string
  emptyDescription: string
  enableSearchAndPagination?: boolean
  searchPlaceholder?: string
}

function createInitialState(fields: CrudField[]) {
  return fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = field.type === 'boolean' ? 'false' : ''
    return acc
  }, {})
}

export function CrudWorkspace<T extends { id?: string; updatedAt?: unknown; title?: string; name?: string }>({
  title,
  description,
  fields,
  items,
  onSave,
  onDelete,
  onReset,
  emptyTitle,
  emptyDescription,
  enableSearchAndPagination = false,
  searchPlaceholder = 'Cari data...',
}: CrudWorkspaceProps<T>) {
  const initialState = useMemo(() => createInitialState(fields), [fields])
  const [form, setForm] = useState<Record<string, any>>(initialState)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const filteredItems = useMemo(() => {
    if (!enableSearchAndPagination) return items

    const normalizedSearch = searchTerm.trim().toLowerCase()
    if (!normalizedSearch) return items

    return items.filter((item) => {
      const values = fields.map((field) => (item as Record<string, unknown>)[field.key])
      values.push(item.id ?? '', item.title ?? '', item.name ?? '')

      return values.some((value) => {
        if (value == null) return false
        if (Array.isArray(value)) {
          return value.join(' ').toLowerCase().includes(normalizedSearch)
        }
        return String(value).replace(/<[^>]*>/g, '').toLowerCase().includes(normalizedSearch)
      })
    })
  }, [enableSearchAndPagination, fields, items, searchTerm])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize))
  const paginatedItems = useMemo(() => {
    if (!enableSearchAndPagination) return filteredItems
    const start = (currentPage - 1) * pageSize
    return filteredItems.slice(start, start + pageSize)
  }, [currentPage, enableSearchAndPagination, filteredItems, pageSize])

  const pageStart = filteredItems.length ? (currentPage - 1) * pageSize + 1 : 0
  const pageEnd = filteredItems.length
    ? Math.min(currentPage * pageSize, filteredItems.length)
    : 0

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, pageSize])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const startCreate = () => {
    setEditingId(null)
    setForm(initialState)
    setIsDialogOpen(true)
  }

  const startEdit = (item: T) => {
    const next = createInitialState(fields)
    fields.forEach((field) => {
      next[field.key] = String((item as Record<string, unknown>)[field.key] ?? (field.type === 'boolean' ? 'false' : ''))
    })
    next.id = item.id ?? ''
    setEditingId(item.id ?? null)
    setForm(next)
    setIsDialogOpen(true)
  }

  const submit = () => {
    onSave(form)
    setIsDialogOpen(false)
  }

  const deleteItem = items.find((i) => i.id === deleteId)
  const deleteName = deleteItem?.title || deleteItem?.name || deleteItem?.id

  return (
    <SectionCard
      title={title}
      description={description}
      action={
        <div className="flex flex-wrap gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 transition shadow-lg shadow-orange-500/10 active:scale-95"
            onClick={startCreate}
            type="button"
          >
            <Plus className="h-4 w-4" />
            Tambah Data
          </button>
          {onReset ? (
            <button
              className="inline-flex items-center gap-2 rounded-2xl border border-stone-800 bg-transparent px-4 py-2 text-sm font-medium text-stone-400 hover:bg-stone-900 transition"
              onClick={onReset}
              type="button"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          ) : null}
        </div>
      }
    >
      <div className="w-full">
        {enableSearchAndPagination ? (
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-2xl border border-stone-800 bg-stone-900/60 py-3 pl-11 pr-4 text-sm text-stone-100 outline-none transition focus:border-orange-500/50 focus:bg-stone-900"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-stone-400">
              <span>Tampilkan</span>
              <select
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="rounded-xl border border-stone-800 bg-stone-900/60 px-3 py-2 text-sm text-stone-100 outline-none transition focus:border-orange-500/50"
              >
                {[5, 10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>per halaman</span>
            </div>
          </div>
        ) : null}
        <DataTable
          rows={paginatedItems}
          columns={[
            ...fields.slice(0, 3).map((field) => ({
              header: field.label,
              render: (row: T) => {
                const rawValue = (row as Record<string, unknown>)[field.key]
                const value = Array.isArray(rawValue)
                  ? rawValue.join(', ')
                  : String(rawValue ?? '-')
                if (field.key === 'status') {
                  return <StatusBadge value={value} />
                }
                if (field.type === 'image') {
                  return (
                    <div className="h-10 w-16 overflow-hidden rounded-lg bg-stone-900 border border-stone-800">
                      {value !== '-' ? (
                        <img src={value} alt="Preview" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] text-stone-600">NO IMG</div>
                      )}
                    </div>
                  )
                }
                return (
                  <span className={cn('text-sm text-stone-400 max-w-[200px] truncate block', (field.key === 'title' || field.key === 'name') && 'font-semibold text-stone-100')}>
                    {value.replace(/<[^>]*>/g, '')}
                  </span>
                )
              },
            })),
            {
              header: 'Updated',
              render: (row: T) => (
                <span className="text-xs text-stone-500 italic">
                  {formatDateTime(row.updatedAt as any)}
                </span>
              ),
            },
            {
              header: 'Action',
              render: (row: T) => (
                <div className="flex gap-2">
                  <button
                    className="inline-flex items-center gap-1 rounded-xl border border-stone-800 bg-transparent px-3 py-2 text-xs font-medium text-stone-300 hover:bg-stone-900 transition"
                    onClick={() => startEdit(row)}
                    type="button"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl border border-rose-900/30 bg-rose-500/5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-500/10 transition"
                    onClick={() => row.id && setDeleteId(row.id)}
                    type="button"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Hapus
                  </button>
                </div>
              ),
            },
          ]}
          emptyDescription={emptyDescription}
          emptyTitle={emptyTitle}
        />
        {enableSearchAndPagination && filteredItems.length ? (
          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-sm text-stone-500">
              Menampilkan <span className="font-semibold text-stone-300">{pageStart}</span>-
              <span className="font-semibold text-stone-300">{pageEnd}</span> dari{' '}
              <span className="font-semibold text-stone-300">{filteredItems.length}</span> data
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-2 rounded-xl border border-stone-800 bg-stone-900/60 px-3 py-2 text-sm font-medium text-stone-300 transition hover:bg-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <span className="min-w-[90px] text-center text-sm text-stone-400">
                Halaman {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage >= totalPages}
                className="inline-flex items-center gap-2 rounded-xl border border-stone-800 bg-stone-900/60 px-3 py-2 text-sm font-medium text-stone-300 transition hover:bg-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit Data' : 'Buat Data Baru'}</DialogTitle>
          <DialogDescription>
            {editingId ? 'Ubah informasi dari data ' : 'Isi form di bawah untuk membuat data baru pada '} 
            <span className="font-semibold text-orange-400">{title}</span>.
          </DialogDescription>
          <DialogCloseButton onClick={() => setIsDialogOpen(false)} />
        </DialogHeader>
        
        <DialogContent className="space-y-5">
          {fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <label className="block text-sm font-semibold text-stone-200 tracking-wide">
                {field.label}
              </label>
              {field.type === 'richtext' ? (
                <RichTextEditor
                  value={form[field.key] ?? ''}
                  onChange={(val) => setForm((prev) => ({ ...prev, [field.key]: val }))}
                  placeholder={field.placeholder}
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  className="min-h-[110px] w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 placeholder-stone-500 outline-none ring-0 transition focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  placeholder={field.placeholder}
                  value={form[field.key] ?? ''}
                />
              ) : field.type === 'select' ? (
                <select
                  className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 outline-none transition focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 appearance-none cursor-pointer"
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  value={form[field.key] ?? ''}
                >
                  <option value="" className="bg-[#1a1a1a] text-stone-400">Pilih {field.label}</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option} className="bg-[#1a1a1a] text-stone-100">
                      {option}
                    </option>
                  ))}
                </select>
              ) : field.type === 'image' ? (
                <ImageUpload
                  folder={`cms/${field.key}`}
                  onChange={(url) =>
                    setForm((prev) => ({ ...prev, [field.key]: url }))
                  }
                  value={form[field.key] ?? ''}
                />
              ) : field.type === 'file' ? (
                <FileUpload
                  folder="cms/manuals"
                  onChange={(url) =>
                    setForm((prev) => ({ ...prev, [field.key]: url }))
                  }
                  value={form[field.key] ?? ''}
                />
              ) : field.type === 'boolean' ? (
                <div className="flex items-center gap-3 py-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form[field.key] === 'true'}
                    onClick={() => setForm((prev) => ({ ...prev, [field.key]: prev[field.key] === 'true' ? 'false' : 'true' }))}
                    className={cn(
                      "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                      form[field.key] === 'true' ? "bg-orange-500" : "bg-stone-700"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out",
                        form[field.key] === 'true' ? "translate-x-5" : "translate-x-0"
                      )}
                    />
                  </button>
                  <span className="text-sm font-medium text-stone-300">{form[field.key] === 'true' ? 'Ya (Aktif)' : 'Tidak'}</span>
                </div>
              ) : field.type === 'json' ? (
                <div className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-xs text-orange-300 font-mono overflow-auto max-h-[200px]">
                  <pre>{JSON.stringify(form[field.key], null, 2)}</pre>
                </div>
              ) : (
                <input
                  className={cn(
                    "w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 placeholder-stone-500 outline-none transition focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20",
                    field.readOnly && "opacity-60 cursor-not-allowed bg-stone-900"
                  )}
                  onChange={(event) =>
                    !field.readOnly && setForm((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  placeholder={field.placeholder}
                  readOnly={field.readOnly}
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                  value={form[field.key] ?? ''}
                />
              )}
            </div>
          ))}
        </DialogContent>
        
        <DialogFooter>
          <button
            className="rounded-xl border border-stone-700 px-5 py-2.5 text-sm font-medium text-stone-300 hover:bg-stone-800 transition"
            onClick={() => setIsDialogOpen(false)}
            type="button"
          >
            Batal
          </button>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 hover:bg-orange-600 transition-all active:scale-95"
            onClick={submit}
            type="button"
          >
            <Save className="h-4 w-4" />
            {editingId ? 'Simpan' : 'Buat Data'}
          </button>
        </DialogFooter>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={deleteId !== null}
        itemName={deleteName}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) onDelete(deleteId)
        }}
      />
    </SectionCard>
  )
}
